import {yupResolver} from '@hookform/resolvers/yup';
import React, {useMemo} from 'react';
import {useForm} from 'react-hook-form';
import {toast} from 'react-hot-toast';
import {useNavigate, useParams} from 'react-router-dom';
import * as yup from 'yup';

import {useUpdateJobMutation} from '../../api/employer';
import {
  expEnum,
  expLvl,
  jobPublishEnum,
  jobPublishOptions,
  jobShiftJson,
  salaryPeriodEnum,
  workPrefEnum,
} from '../../utils/constants';
import Button from '../ui/Button';
import {SelectFieldHookForm} from '../ui/SelectField';
import ApplicantDetailsForm from './ApplicantDetailsForm';
import JobDetailsForm from './JobDetailsForm';
import SelectSkillForm from './SelectSkillForm';

const jobDetailSchema = yup.object().shape({
  /* Job Details */
  title: yup.string().required('title is required'),
  description: yup.string().required('job description is required'),
  requirements: yup.string().required('job requirements is required'),
  responsiblities: yup.string().required('job responsiblities is required'),
  companyInfo: yup.string().required('company information is required'),
  expLevel: yup.object().nullable().required('experience level is required'),
  jobType: yup.object().nullable().required('Please select job type'),
  jobShift: yup.object().nullable().required('Please select job shift'),
  requiredExp: yup
    .object()
    .nullable()
    .required('Please select experience level'),
  qualifications: yup.string().required('qualifications are required'),
  salaryPeriod: yup.object().nullable().required('select salary period'),
  salary: yup
    .number()
    .min(1)
    .typeError('salary is required')
    .required('salary is required'),
  vacancies: yup
    .number()
    .min(1)
    .typeError('vacancies are required')
    .required('vacancies are required'),
  country: yup.object().nullable().required(),
  state: yup.object().nullable().required(),
  city: yup.string().required(),

  /* applicant details */
  autoReplay: yup.string().required(),
});

const JobForm = ({jobDetails, skillList, conpaniesOptions}) => {
  const {jobId} = useParams();
  const navigate = useNavigate();
  const {mutate: updateJobMutation, isLoading: isJobUpdating} =
    useUpdateJobMutation(jobId);

  // set intial form values
  const initialFormValue = useMemo(() => {
    const payload = {
      title: jobDetails?.title || '',
      description: jobDetails?.description || '',
      requirements: jobDetails?.requirements || '',
      responsiblities: jobDetails.responsiblities || '',
      companyInfo: jobDetails?.companyInfo || '',

      expLevel: jobDetails?.expLevel
        ? {
            value: +jobDetails.expLevel,
            label: expLvl[+jobDetails.expLevel],
          }
        : null,

      jobType: jobDetails?.jobType
        ? {value: +jobDetails.jobType, label: workPrefEnum[+jobDetails.jobType]}
        : null,

      jobShift: jobDetails?.jobShift
        ? {
            value: jobDetails.jobShift,
            label: jobShiftJson[jobDetails.jobShift],
          }
        : null,

      requiredExp: jobDetails?.requiredExp
        ? {
            value: jobDetails.requiredExp,
            label: expEnum[jobDetails.requiredExp],
          }
        : null,
      qualifications: jobDetails?.qualifications || '',

      salaryPeriod: jobDetails?.salaryPeriod
        ? {
            value: jobDetails.salaryPeriod,
            label: salaryPeriodEnum[jobDetails.salaryPeriod],
          }
        : null,
      salary: +jobDetails?.salary || '',
      vacancies: +jobDetails?.vacancies || '',

      city: jobDetails?.city || '',
      state: jobDetails?.state
        ? {label: jobDetails.state, value: jobDetails.state}
        : null,
      country: jobDetails?.country
        ? {label: jobDetails.country, value: jobDetails.country}
        : null,

      name: String(jobDetails?.applicantDetails?.name) || '3',
      email: String(jobDetails?.applicantDetails?.email) || '3',
      cv: String(jobDetails?.applicantDetails?.cv) || '3',
      phone: String(jobDetails?.applicantDetails?.phone) || '3',
      coverLatter: String(jobDetails?.applicantDetails?.coverLatter) || '3',
      autoReplay: jobDetails?.applicantDetails?.autoReplay || '',

      technicalSkills: (jobDetails?.technicalSkills || []).map(({_id}) => _id),
      interpersonalSkills: (jobDetails?.interpersonalSkills || []).map(
        ({_id}) => _id,
      ),

      // remove this static details
      companyId: jobDetails?.company
        ? {label: jobDetails.company.name, value: jobDetails.company._id}
        : null,
      status: jobDetails?.status
        ? {
            label: jobPublishEnum[String(jobDetails.status)],
            value: String(jobDetails.status),
          }
        : null,
    };

    return payload;
  }, [jobDetails]);

  const jobFormHook = useForm({
    resolver: yupResolver(jobDetailSchema),
    mode: 'all',
    defaultValues: initialFormValue,
  });
  const {handleSubmit, reset: resetForm, control} = jobFormHook;

  const jobUpdateMutationOptions = {
    onSuccess: () => {
      toast.success('Job updated Successfully');
      resetForm();
      navigate('/jobs');
    },
    onError: (data) => toast.error(data.response.data.message),
  };

  const handleFormSubmission = (values) => {
    const {
      name,
      email,
      phone,
      cv,
      coverLatter,
      autoReplay,

      country: {value: country},
      expLevel: {value: expLevel},
      jobShift: {value: jobShift},
      jobType: {value: jobType},
      salaryPeriod: {value: salaryPeriod},
      state: {value: state},
      requiredExp: {value: requiredExp},
      status: {value: status},
      companyId: {value: companyId},

      ...restFormValues
    } = values;

    const payload = {
      applicantDetails: {
        name: +name,
        email: +email,
        phone: +phone,
        cv: +cv,
        coverLatter: +coverLatter,
        autoReplay,
      },

      country,
      expLevel: +expLevel,
      jobShift,
      jobType: +jobType,
      salaryPeriod,
      state,
      requiredExp,

      status: status === 'true',
      companyId,

      ...restFormValues,
    };

    updateJobMutation({jobId, payload}, jobUpdateMutationOptions);
  };

  return (
    <div className="">
      <form className="" onSubmit={handleSubmit(handleFormSubmission)}>
        <div className="flex justify-between items-center border bg-white border-primary-gray px-8 py-5 sticky top-0 left-0 z-10">
          <h3 className="text-2xl font-semibold text-center">Edit Job</h3>
          <div className="flex gap-3">
            <SelectFieldHookForm
              name="status"
              className="w-full min-w-[180px] bg-white"
              control={control}
              options={jobPublishOptions}
            />
            <SelectFieldHookForm
              name="companyId"
              className="w-full min-w-[250px] bg-white"
              control={control}
              options={conpaniesOptions}
            />
            <Button type="submit" className="w-[300px]" loading={isJobUpdating}>
              Update Job
            </Button>
          </div>
        </div>

        <div className="w-full px-8 pt-5 pb-8">
          <div className="bg-white p-12">
            <div className="flex flex-col gap-4 max-w-[80%]">
              <h3 className="text-2xl font-semibold pb-[4px]">Job Details</h3>
              <div>
                <JobDetailsForm jobFormHook={jobFormHook} />
              </div>
            </div>
          </div>

          <div className="bg-white p-12 mt-10">
            <div className="flex flex-col gap-4 max-w-[80%]">
              <h3 className="text-2xl font-semibold">Required skills</h3>
              <SelectSkillForm
                jobFormHook={jobFormHook}
                skillList={skillList}
              />
            </div>
          </div>

          <div className="bg-white p-12 mt-10">
            <div className="flex flex-col gap-4 max-w-[80%]">
              <div className="flex flex-col gap-4">
                <div>
                  <h3 className="text-2xl font-semibold pb-[4px] mb-6">
                    Applicant details
                  </h3>
                  <ApplicantDetailsForm jobFormHook={jobFormHook} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};

export default JobForm;
